import {
  IndividualDetailsPost,
  LinkIndividualPostPartial
} from '@interfaces/Individual.interface';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  CompanyDetailsPOST,
  CompanyInterface
} from '@interfaces/Company.interface';
import type ApiClient from '../ApiClient';

export class ApiCompanies extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CompanyInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/companies', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async get(id: number): Promise<CompanyInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/company/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async archive(ids: number[]) {
    return this.client.delete('/company-bulk-delete', {
      data: { ids: ids.map((value: number) => ({ id: value })) }
    });
  }

  async unlinkIndividual(
    individualId: string | number,
    companyId?: string | number
  ) {
    return this.client.delete(
      `/companies/${companyId}/individuals/${individualId}`
    );
  }

  async storeLinkNewIndividual(
    companyId: number | string | undefined,
    dto: IndividualDetailsPost
  ) {
    await this.client.post(`companies/${companyId}/individuals`, dto);
  }

  async updateIndividualPrimaryOrJobTitle(
    companyId: string | undefined,
    individual: number,
    dto: LinkIndividualPostPartial
  ): Promise<void> {
    await this.client.put(
      `companies/${companyId}/individuals/${individual}`,
      dto
    );
  }

  async updateCompanyAddressAndWebsite(
    companyId: string | undefined,
    dto: Pick<CompanyDetailsPOST, 'address' | 'website'>
  ) {
    await this.client.patch(`companies/${companyId}/address`, dto, {
      headers: { Version: '2025-11-14' }
    });
  }
}

export { companiesQueryKeys } from './query-keys';
export type { CompaniesListQueryParams } from './query-keys';
export { useCompaniesListQuery, useCompanyQuery } from './use-companies-queries';
export {
  useCompaniesArchiveMutation,
  useCompaniesStoreLinkNewIndividualMutation,
  useCompaniesUnlinkIndividualMutation,
  useCompaniesUpdateAddressAndWebsiteMutation,
  useCompaniesUpdateLinkedIndividualMutation
} from './use-companies-mutations';
export type {
  CompaniesStoreLinkNewIndividualVariables,
  CompaniesUnlinkIndividualVariables,
  CompaniesUpdateAddressAndWebsiteVariables,
  CompaniesUpdateLinkedIndividualVariables
} from './use-companies-mutations';
